import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSession, useLogout } from '../auth/session';
import { useDebounced } from '../hooks/useDebounced';
import { useShortcuts } from '../hooks/useShortcuts';
import { useTheme } from '../theme/ThemeProvider';
import {
  useCreateSnippet,
  useDeleteSnippet,
  useSnippets,
  useTags,
  type LibraryFilters,
} from '../library/queries';
import { useTeams } from '../library/teamQueries';
import { useTeamBilling } from '../library/billingQueries';
import { LibrarySidebar } from '../library/LibrarySidebar';
import { libraryLabel, type Library } from '../library/LibrarySwitcher';
import { SnippetList } from '../library/SnippetList';
import { SnippetDetail } from '../library/SnippetDetail';
import { CommandPalette, type Command } from '../components/CommandPalette';
import { Banner, EmptyState } from '../components/ui';

export function LibraryPage() {
  const { data: user } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();

  const [library, setLibrary] = useState<Library>({ scope: 'Personal' });
  const [filters, setFilters] = useState<LibraryFilters>({});
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);
  const debouncedSearch = useDebounced(search);

  const query = useMemo<LibraryFilters>(
    () => ({
      ...filters,
      q: debouncedSearch.trim() || undefined,
      // Ranking only means something once there is something to rank against.
      sort: debouncedSearch.trim() ? 'Relevance' : 'Updated',
      scope: library.scope,
      teamId: library.teamId,
      // Stated rather than left to the API's default, so this page keeps listing scripts even if
      // that default ever changes.
      kind: 'Script',
    }),
    [filters, debouncedSearch, library],
  );

  const snippets = useSnippets(query);
  const tags = useTags(library.scope === 'Team' ? library.teamId : undefined);
  const teams = useTeams();
  const createSnippet = useCreateSnippet();
  const deleteSnippet = useDeleteSnippet();

  // A team library whose subscription has lapsed still reads, so this is only ever about writing.
  const teamBilling = useTeamBilling(
    library.scope === 'Team' ? (library.teamId ?? null) : null,
  );

  const teamLocked = teamBilling.data?.state === 'Locked';

  // Creating content needs a confirmed address; reading does not.
  const canEdit = user?.emailConfirmed === true;

  // "Shared with me" holds other people's snippets, so there is nothing to create there.
  const canCreateHere = canEdit && library.scope !== 'SharedWithMe' && !teamLocked;

  const items = snippets.data?.items ?? [];
  const selected = selectedId ?? items[0]?.id ?? null;

  const onCreate = useCallback(() => {
    if (!canCreateHere) {
      return;
    }

    createSnippet.mutate(
      // New snippets land in whichever library is open, which is what the sidebar just promised.
      { title: 'Untitled snippet', teamId: library.scope === 'Team' ? library.teamId : undefined },
      { onSuccess: (snippet) => setSelectedId(snippet.id) },
    );
  }, [canCreateHere, createSnippet, library]);

  useShortcuts({
    onFocusSearch: () => searchRef.current?.focus(),
    onTogglePalette: () => setPaletteOpen((open) => !open),
  });

  const commands = useMemo<Command[]>(
    () => [
      { id: 'new', label: 'New snippet', hint: 'Command', run: onCreate },
      { id: 'theme', label: 'Toggle light / dark theme', hint: 'Command', run: toggleTheme },
      {
        id: 'search',
        label: 'Focus search',
        hint: 'Command',
        run: () => searchRef.current?.focus(),
      },
      { id: 'teams', label: 'Manage teams', hint: 'Command', run: () => void navigate('/teams') },
      {
        id: 'personal',
        label: 'Go to my drawer',
        hint: 'Library',
        run: () => setLibrary({ scope: 'Personal' }),
      },
      {
        id: 'shared',
        label: 'Go to shared with me',
        hint: 'Library',
        run: () => setLibrary({ scope: 'SharedWithMe' }),
      },
      ...(teams.data ?? []).map<Command>((team) => ({
        id: `team:${team.id}`,
        label: `Go to ${team.name}`,
        hint: 'Library',
        run: () => setLibrary({ scope: 'Team', teamId: team.id }),
      })),
      { id: 'signout', label: 'Sign out', hint: 'Command', run: () => logout.mutate() },
    ],
    [onCreate, toggleTheme, logout, navigate, teams.data],
  );

  return (
    <div className="flex h-full flex-col md:flex-row">
      <LibrarySidebar
        filters={filters}
        tags={tags.data ?? []}
        teams={teams.data ?? []}
        library={library}
        section="scripts"
        onFiltersChange={setFilters}
        onLibraryChange={(next) => {
          setLibrary(next);

          // Back to All snippets. Staying in Trash — or keeping a tag filter — carries a view
          // that belonged to the previous library into one where it usually matches nothing, so
          // the switch looks like an empty library rather than a filtered one. Tags in
          // particular belong to a library and do not travel.
          setFilters({});

          // A snippet from the previous library is not in this one.
          setSelectedId(null);
        }}
        onManageTeams={() => void navigate('/teams')}
      />

      {!canEdit && (
        <div className="order-first p-3 md:hidden">
          <Banner tone="info">Confirm your email address to start creating snippets.</Banner>
        </div>
      )}

      <SnippetList
        ref={searchRef}
        items={items}
        total={snippets.data?.total ?? 0}
        fuzzy={snippets.data?.fuzzy ?? false}
        query={search}
        selectedId={selected}
        isPending={snippets.isPending}
        canCreate={canCreateHere}
        libraryName={libraryLabel(library, teams.data ?? [])}
        onQueryChange={setSearch}
        onSelect={setSelectedId}
        onCreate={onCreate}
      />

      <main className="min-h-0 min-w-0 flex-1">
        {!canEdit && (
          <div className="hidden p-3 md:block">
            <Banner tone="info">
              Confirm your email address to start creating and editing snippets.
            </Banner>
          </div>
        )}

        {/* Said once, up front. A library that silently refuses every save reads as a broken
            editor rather than as a billing problem. */}
        {teamBilling.data && teamBilling.data.state !== 'Entitled' && (
          <div className="p-3">
            <Banner tone="error">
              {teamLocked
                ? 'This team library is read-only until its subscription is sorted out. '
                : 'A payment for this team did not go through. '}
              <button
                type="button"
                className="underline underline-offset-2"
                onClick={() => void navigate('/teams')}
              >
                Open team settings
              </button>
            </Banner>
          </div>
        )}

        {selected ? (
          <SnippetDetail
            key={selected}
            snippetId={selected}
            availableTags={tags.data ?? []}
            onDelete={(id) => deleteSnippet.mutate(id)}
            onDeleted={() => setSelectedId(null)}
          />
        ) : (
          <EmptyState title="Nothing selected">
            Pick a snippet from the list, or press ⌘K to jump to one.
          </EmptyState>
        )}
      </main>

      <CommandPalette
        open={paletteOpen}
        commands={commands}
        snippets={items}
        onClose={() => setPaletteOpen(false)}
        onOpenSnippet={setSelectedId}
      />
    </div>
  );
}
